import {createContext, useCallback, useContext, useEffect, useState} from "react";
import { checkAuth } from "../utilities/authUtils";
import { logout as logoutAPI, login as loginAPI } from "../services/ledgerService.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [user, setUser] = useState(null);

    // refreshAuth re-checks the session against the server — the auth cookie is
    // the source of truth — and syncs isAuthenticated. Safe to call often: it
    // only triggers a re-render when the value actually changes. Auth
    // transitions that happen outside login()/logout() (registration, the OAuth
    // callback) set the cookie server-side; calling this (e.g. on navigation —
    // see RootLayout) lets the client catch up without a full page reload.
    const refreshAuth = useCallback(async () => {
        try {
            const ok = await checkAuth();
            setIsAuthenticated(ok);
            if (!ok) {
                setUser(null);
            }
            return ok;
        } catch (e) {
            setIsAuthenticated(false);
            return false;
        }
    }, []);

    // Verify on mount, and again when the tab regains focus / becomes visible —
    // this catches logins/logouts in other tabs and sessions that expired while
    // the tab was backgrounded.
    useEffect(() => {
        refreshAuth();

        const onFocus = () => refreshAuth();
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                refreshAuth();
            }
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisible);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onVisible);
        };
    }, [refreshAuth]);

    const loginWithGoogle = () => {
        window.location.href = "/auth/login"
    };

    const logout = async () => {
        await logoutAPI();
        setIsAuthenticated(false);
        setUser(null);
    }

    const login = async ({ email, password }) => {
        const u = await loginAPI({ email, password });
        setIsAuthenticated(true);
        setUser(u);
        return u;
    }

    return (
        <AuthContext.Provider value={{
            isAuthenticated,
            user,
            refreshAuth,
            loginWithGoogle,
            logout,
            login,
        }}>
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
